import { defineComponent as _defineComponent } from 'vue'
import CategoryProductFlyoverFiltering from './CategoryProductFlyoverFiltering.vue';
import CategoryProductSortByDropdown from './CategoryProductSortByDropdown.vue';
import { store } from '@/store';
import { useTranslations } from '@/core/translate.mixin';
import { ProductTileView, FilterAndSort, FilterAndSortRequestType } from '@/enums';
import { computed, onMounted, ref, watch } from 'vue';
import { watchRelativeUrl } from '@/core/route.service';
import urlHelper, { UrlQueryKey } from '@/router/urlHelper';
import Http from '@/core/http/http.service';
import dictionaryService from '@/core/dictionary.service';
import queryParamsService from '@/core/query-params.service';
import FacetSearchViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.FacetSearch.FacetSearchViewModel;
import FilterAndSortViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.FacetSearch.FilterAndSortViewModel;
import FilterAndSortItemViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.FacetSearch.FilterAndSortItemViewModel;

interface SelectedParameters {
    selectedFacets: { [key: string]: string[] },
    selectedCategoryCode: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CategoryPageB2C',
  props: {
    headline: null,
    categoryCode: null,
    showTallProductImages: { type: Boolean, default: false },
    productCodes: null,
    useProductCodesAsFilter: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { headline: string, categoryCode: string, showTallProductImages: boolean, productCodes: string[], useProductCodesAsFilter?: boolean }



const sortByLabel = ref(null);
const labelWidth = ref(0);

const productTileView = computed<ProductTileView>(() => store.getters['catalog/productTileView']);
const view = ref<ProductTileView | null>(ProductTileView.Product);

onMounted(() => {
    if (productTileView.value) {
        view.value = productTileView.value;
    }
    selectedFacets.value = urlHelper.getFacets();
    selectedCategoryCode.value = getCategoryCode();
    labelWidth.value = (sortByLabel.value as HTMLElement).offsetWidth;
});

function setProductTileView() {
    store.dispatch('catalog/toggleProductTileView', view.value);
};

const filterOptions: FilterToggleOption[] =
    [
        { text: dictionaryService.get('Pages.Category.Views.ProductLabel'), value: ProductTileView.Product },
        { text: dictionaryService.get('Pages.Category.Views.RoomLabel'), value: ProductTileView.Room }
    ];

const searchedProducts = ref<ProductTileViewModel[]>([]);
const pending = ref<boolean>(false);
const total = ref<number>(0);
const preventRouteUpdates = ref<boolean>(false);

const sortAndFilters = ref<FilterAndSortViewModel[]>([]);
const sortMode = ref<string>(FilterAndSort.Bestsellers);
const lastFacet = ref<string>();
const sortOptions = ref<FilterAndSortItemViewModel[]>([]);
const totalFilters = computed<number>(() => {
    let count = 0;
    if (selectedFacets.value) {
        Object.keys(selectedFacets.value).forEach(key => {
            count += selectedFacets.value[key].length;
        });
    }
    return count;
});

const selectedFacets = ref<{ [key: string]: string[] }>();
const selectedCategoryCode = ref<string>();

const borderColor = computed<OpacityFunction>(() => store.getters['app/borderColor']);

function getCategoryCode() {
    const queryParam = queryParamsService.getParam(UrlQueryKey.categoryCodeKey);
    return queryParam || props.categoryCode;
}

const parameters = computed<SelectedParameters>(() => {
    return {
        selectedFacets: selectedFacets.value,
        selectedCategoryCode: selectedCategoryCode.value
    };
});

async function getSearchByFacets(parameters?: SelectedParameters, oldParameters?: SelectedParameters): Promise<void> {
    if (!parameters) return;
    if (preventRouteUpdates.value) {
        return;
    }

    pending.value = true;
    lastFacet.value = getLastFacet();

    const sortValue = urlHelper.getQueryParam(UrlQueryKey.sortKey);
    if (sortValue) sortMode.value = sortValue;
    const request = urlHelper.getRequestModel(parameters.selectedCategoryCode, lastFacet.value, props.productCodes, sortMode.value, props.useProductCodesAsFilter);
    setProductTileColorByFilterIfAny();

    Object.keys(selectedFacets.value).forEach(facetKey => {
        request[facetKey] = selectedFacets.value[facetKey].join('|');
    });

    if (preventRouteUpdates.value) return;

    Http.get<FacetSearchViewModel>('search/facetsearch', request).then((response) => {
        total.value = response.total;
        searchedProducts.value = response.products;
        pending.value = false;
        sortAndFilters.value = response.sortAndFilters;
        sortOptions.value = response.sortAndFilters.find(x => x.requestType.toString() === FilterAndSortRequestType.Sort.toString()).items;
    });
}

function getLastFacet(): string | undefined {
    return queryParamsService.getParam(UrlQueryKey.lastFacetKey);
}

watch(parameters, getSearchByFacets, { deep: true });

function routeChanged(route: RelativeUrl, oldRoute: RelativeUrl) {
    preventRouteUpdates.value = route !== undefined && oldRoute !== undefined && route.path !== oldRoute.path;
    if (route && oldRoute && route.path === oldRoute.path) {
        selectedFacets.value = urlHelper.getFacets();
        selectedCategoryCode.value = getCategoryCode();
    }
}

watchRelativeUrl(routeChanged);

const filterOverlayOpen = ref<boolean>(false);

const { t } = useTranslations();
const totalLabel = computed<string>(() => `${total.value} ` + t('Pages.Category.Flyover.Products'));

function openFilterOverlay() : void {
    filterOverlayOpen.value = true;
}

function closeFilterOverlay() : void {
    filterOverlayOpen.value = false;
}

function setProductTileColorByFilterIfAny() {
    store.dispatch('catalog/setProductTileColorFilters', []);
    if (selectedFacets.value) {
        Object.keys(selectedFacets.value).forEach(key => {
            const facet = key.toLowerCase();
            // Since the FE does not know about the facet types (generic) we get it like this.
            // Can perhaps be refactored to have a more tight coupling.
            if (facet === 'finishfiltercolors') {
                const colors = selectedFacets.value[key];
                store.dispatch('catalog/setProductTileColorFilters', colors);
            }
        });
    }
}

return { __sfc: true,props, sortByLabel, labelWidth, productTileView, view, setProductTileView, filterOptions, searchedProducts, pending, total, preventRouteUpdates, sortAndFilters, sortMode, lastFacet, sortOptions, totalFilters, selectedFacets, selectedCategoryCode, borderColor, getCategoryCode, parameters, getSearchByFacets, getLastFacet, routeChanged, filterOverlayOpen, t, totalLabel, openFilterOverlay, closeFilterOverlay, setProductTileColorByFilterIfAny, CategoryProductFlyoverFiltering, CategoryProductSortByDropdown }
}

})